import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  IdCard, Save, Loader2, RefreshCw, Info, CheckCircle, 
  AlertCircle, Sparkles, Copy, Check
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PatientIdSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [newPrefix, setNewPrefix] = useState('');
  const [previewId, setPreviewId] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchSuggestions();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/patient-id/settings`);
      setSettings(response.data);
      setNewPrefix(response.data.prefix);
      setPreviewId(response.data.sample_id);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load Patient ID settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestions = async () => {
    try {
      const response = await axios.get(`${API}/patient-id/suggest-prefix`);
      setSuggestions(response.data.variations || []);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    }
  };

  const handlePrefixChange = async (prefix) => {
    setNewPrefix(prefix.toUpperCase());
    
    // Generate preview with new prefix
    if (prefix.length >= 2 && /^[A-Za-z]+$/.test(prefix)) {
      try {
        const response = await axios.get(`${API}/patient-id/generate-preview?prefix=${prefix.toUpperCase()}`);
        setPreviewId(response.data.preview_id);
      } catch (error) {
        // Ignore preview errors
      }
    }
  };

  const handleSave = async () => {
    if (newPrefix.length < 2 || newPrefix.length > 6) {
      toast.error('Prefix must be 2-6 letters');
      return;
    }
    
    if (!/^[A-Za-z]+$/.test(newPrefix)) {
      toast.error('Prefix must contain only letters');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/patient-id/settings/prefix`, {
        prefix: newPrefix.toUpperCase()
      });
      
      toast.success(response.data.message);
      setSettings(prev => ({
        ...prev,
        prefix: response.data.prefix,
        sample_id: response.data.sample_id,
        auto_generated: false
      }));
      setPreviewId(response.data.sample_id);
    } catch (error) {
      console.error('Failed to save prefix:', error);
      toast.error(error.response?.data?.detail || 'Failed to save prefix');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Main Settings Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-teal-500 to-blue-600 rounded-lg">
              <IdCard className="h-5 w-5 text-white" />
            </div>
            <div>
              <CardTitle>Patient ID Configuration</CardTitle>
              <CardDescription>
                Customize how Patient IDs are generated for your clinic
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Format Explanation */}
          <Alert className="bg-blue-50 border-blue-200">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              <strong>Smart Patient ID Format:</strong> <code className="bg-blue-100 px-2 py-0.5 rounded">PREFIX-YYMM-SEQUENCE-CHECK</code>
              <br />
              <span className="text-sm">
                • <strong>PREFIX</strong>: Your clinic identifier (2-6 letters)
                • <strong>YYMM</strong>: Year & month of registration
                • <strong>SEQUENCE</strong>: Auto-incrementing number
                • <strong>CHECK</strong>: Validation digit (prevents typos)
              </span>
            </AlertDescription>
          </Alert>

          {/* Current Preview */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl p-6 text-center">
            <p className="text-gray-400 text-sm mb-2">Next Patient ID Preview</p>
            <div className="flex items-center justify-center gap-3">
              <span className="text-3xl font-mono font-bold text-white tracking-wider">
                {previewId}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard(previewId)}
                className="text-gray-400 hover:text-white"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <div className="flex items-center justify-center gap-2 mt-3">
              <Badge variant="outline" className="text-gray-400 border-gray-600">
                <CheckCircle className="h-3 w-3 mr-1 text-green-500" />
                Validated
              </Badge>
              {settings?.auto_generated && (
                <Badge variant="outline" className="text-amber-400 border-amber-600">
                  <Sparkles className="h-3 w-3 mr-1" />
                  Auto-generated
                </Badge>
              )}
            </div>
          </div>

          {/* Prefix Configuration */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="prefix" className="text-base font-medium">
                Clinic Prefix
              </Label>
              <p className="text-sm text-gray-500 mb-2">
                2-6 letters that identify your clinic in Patient IDs
              </p>
              <div className="flex gap-3">
                <Input
                  id="prefix"
                  value={newPrefix}
                  onChange={(e) => handlePrefixChange(e.target.value.replace(/[^A-Za-z]/g, ''))}
                  placeholder="e.g., MED, SMILE, DENT"
                  className="font-mono text-lg uppercase max-w-[200px]"
                  maxLength={6}
                />
                <Button 
                  onClick={handleSave} 
                  disabled={saving || newPrefix === settings?.prefix}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Prefix
                </Button>
              </div>
            </div>

            {/* Suggestions */}
            {suggestions.length > 0 && (
              <div>
                <Label className="text-sm text-gray-500">Suggested prefixes based on your clinic name:</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {suggestions.map((suggestion) => (
                    <Button
                      key={suggestion}
                      variant="outline"
                      size="sm"
                      onClick={() => handlePrefixChange(suggestion)}
                      className={`font-mono ${newPrefix === suggestion ? 'border-teal-500 bg-teal-50' : ''}`}
                    >
                      {suggestion}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Stats */}
          {settings?.stats && (
            <div className="grid grid-cols-3 gap-4 pt-4 border-t">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{settings.stats.total_patients}</p>
                <p className="text-sm text-gray-500">Total Patients</p>
              </div>
              <div className="text-center p-4 bg-teal-50 rounded-lg">
                <p className="text-2xl font-bold text-teal-600">{settings.stats.new_format_count}</p>
                <p className="text-sm text-gray-500">New Format</p>
              </div>
              <div className="text-center p-4 bg-amber-50 rounded-lg">
                <p className="text-2xl font-bold text-amber-600">{settings.stats.legacy_format_count}</p>
                <p className="text-sm text-gray-500">Legacy Format</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Format Details Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Understanding Patient IDs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Visual breakdown */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-center gap-1 text-xl font-mono">
                <span className="bg-teal-100 text-teal-700 px-2 py-1 rounded">{settings?.prefix || 'MED'}</span>
                <span className="text-gray-400">-</span>
                <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded">2502</span>
                <span className="text-gray-400">-</span>
                <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded">0142</span>
                <span className="text-gray-400">-</span>
                <span className="bg-green-100 text-green-700 px-2 py-1 rounded">7</span>
              </div>
              <div className="flex items-center justify-center gap-1 mt-2 text-xs">
                <span className="text-teal-600 px-2">Clinic</span>
                <span className="text-gray-300">|</span>
                <span className="text-blue-600 px-2">Feb 2025</span>
                <span className="text-gray-300">|</span>
                <span className="text-purple-600 px-2">Patient #142</span>
                <span className="text-gray-300">|</span>
                <span className="text-green-600 px-2">Check</span>
              </div>
            </div>

            {/* Benefits */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                <div>
                  <p className="font-medium">Unique & Meaningful</p>
                  <p className="text-sm text-gray-500">Every ID identifies clinic and registration time</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                <div>
                  <p className="font-medium">Error Prevention</p>
                  <p className="text-sm text-gray-500">Check digit catches typos automatically</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                <div>
                  <p className="font-medium">Easy to Communicate</p>
                  <p className="text-sm text-gray-500">Clear format for phone & verbal use</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                <div>
                  <p className="font-medium">Backwards Compatible</p>
                  <p className="text-sm text-gray-500">Existing PAT000XXX IDs still work</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Note about existing patients */}
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <strong>Note:</strong> Changing the prefix only affects <em>new</em> patients. 
          Existing patient IDs remain unchanged to maintain data consistency and avoid 
          breaking references in documents or cards already issued.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default PatientIdSettings;
